import classNames from 'classnames';
import React, { useContext, useMemo, useRef, useState, useCallback, useEffect } from 'react';
import _, { noop, isArray, first, isEmpty } from 'underscore';
import PropTypes from 'prop-types';
import { mergeStyles } from '@fluentui/react';
import { Tab, TabList } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';

import {
  Collapse,
  TextTitle,
  useCollapseState,
  ValidatedPanel,
  ValidatedFixedPanel,
  getPanelErrorProps,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { getFinalUrlCardSummaryDetails } from '@bingads-webui-campaign-react/validated-url-input';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withFinalUrlRecommendationUpdate } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { ValidatedLegacyCallToActionWithLanguage } from '@bingads-webui-campaign-react/legacy-call-to-action';
import {
  CopilotRepositoryProvider,
  withBannerService,
} from '@bingads-webui/inline-copilot';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { entityIs, andComp, propertyIs } from '@bingads-webui-campaign-react/form-error';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import {
  ValidatedFinalUrlWithRecommendations,
  ValidatedStoreBackedBusinessName,
} from '@bingads-webui-campaign-react/pmax-text-input';
import { getODataConfig, ValidatedVideoExperienceWithRecommendationFetch } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withScope } from '@bingads-webui-react/scope-service';
import { getTextCardSummaryDetails } from '@bingads-webui-campaign-react/validated-text-collection';
import {
  ImageListPortalContainerWithControlErrorHandling,
  ImageListSummaryCardDetails,
  ValidatedImageExperienceWrapper,
  PerfMaxAdPreview,
  PerfMaxAdPreviewUAPL,
  PerfMaxImageSuggestionCarousel,
} from '@bingads-webui-campaign-react/pmax-image-experience';
import { AdPlacementsGroupType } from '@bingads-webui-campaign-react/ad-placement-consts';
import { useCopilot } from '@bingads-webui-campaign-react/copilot-experience';
import { DisclaimerAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import Repository from '@bingads-webui-campaign-legacy/adextension-shared/service/repository';
import { getAssetEnhancementSettings } from '@bingads-webui-campaign/image-experience-utils';
import { CampaignFetchProvider } from '@bingads-webui-campaign-react/campaign-http';

import { getImageExperienceStyles } from './styles';
import ImageAssetsImg from './media/image-assets.svg';
import VideoAssetsImg from './media/video-assets.svg';
import FinalUrlImg from './media/final-url.svg';
import LogoAssetsImg from './media/logo-assets.svg';
import BusinessNameImg from './media/business-name.svg';
import TextAssetsImg from './media/text.svg';
import hotspotImg from './media/hotspot.svg';
import CallToActionImg from './media/call-to-action.svg';
import { AdvancedSettings } from './components/advanced-settings';
import { TextAssetCollectionWithRecommendationPrefill } from './components/text-asset-collection-with-recommendation-prefill';
import { TextAssetCollectionWithCoPilot } from './components/text-asset-collection-with-copilot';
import {
  extraPlacementInfo,
  msanMMAConfig,
  msanMMAV2Config,
  responsiveMsanMMAConfig,
  responsiveMsanMMABoostConfigV2,
  msanMMABoostConfig,
  msanMMABoostConfigV2,
  responsiveMsanMMABoostConfig,
  AdPreviewOverideStylesByKey,
  ERROR_ENTITY,
} from './constants';
import { getNativeAdPanels } from './configs';
import { ValidatedHotspot } from './components/validated-hotspot';
import { MultiAdVideoPreview } from './components/multi-ad-video-preview';

const DisplayPlusMaxImageCount = 16;
const DisplayPlusMaxVideoCount = 5;

const TAB_IMAGE_ADS = 'imageAds';
const TAB_VIDEO_ADS = 'videoAds';

const videoTypesWithOrder = ['VideoMedia16X9'];

const advancedSettingsStyles = mergeStyles({
  marginBottom: 40,
});

const nativeAdBusinessNameProp = andComp(entityIs(ERROR_ENTITY.Ad), propertyIs('BusinessName'));
const nativeAdFinalUrlsProp = andComp(entityIs(ERROR_ENTITY.Ad), propertyIs('FinalUrls'));
const ValidatedFinalUrlWithStoreURL = withError(withCustomValidation(withFinalUrlRecommendationUpdate(ValidatedFinalUrlWithRecommendations)));
const ServerValidatedFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedFinalUrlWithStoreURL, { filter: nativeAdFinalUrlsProp }));

const getPlacementCount = (IsAmplifyEnabled, DisplayPlusAmplify, IsMMAV2Enabled) => {
  if (IsAmplifyEnabled && DisplayPlusAmplify) {
    return 11;
  } else if (IsMMAV2Enabled) {
    return 10;
  }
  return 7;
};

export const DisplayPlusNativeAdsInternal = ({
  data: ads,
  editingAdId,
  onDataChange,
  isSaving,
  isCanceling,
  biddingScheme,
  scope,
  activity,
  supportImageFitting,
  setAds,
  loadClipchampPlayer = null,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const perfMarker = useContext(PerformanceMarkerContext);
  const instrumentation = useContext(InstrumentationContext);

  const { cid, aid } = scope.values || {};
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const {
    IsAmplifyEnabled, IsMSANLogoAndCTAForMMAEnabled, IsMSANDisclaimerEnabled, IsDomesticSimplifiedChineseEnabled,
  } = Permissions;

  const useClipchamp = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;

  const {
    DisplayPlusCopilotRecommendations, DisplayPlusAmplify, UAPLNativeAdIntegration, CopilotCTALogoGeneration,
    ImageFittingMVP = false, ImageFittingExlcudeMAE = true, ImageFittingUnifiedImageROI, AdPreviewRefresh,
  } = DynamicPermissions;

  const isMMAV2Enabled = Permissions.IsMMAV2Enabled && !(IsAmplifyEnabled && DisplayPlusAmplify);

  const stylingProps = useMemo(() =>
  (
    {
      component: {
        placements: getPlacementCount(DisplayPlusAmplify, IsAmplifyEnabled, isMMAV2Enabled),
        isMMAV2Enabled,
      },
    }
  ), [DisplayPlusAmplify, IsAmplifyEnabled, isMMAV2Enabled]);

  const getClasses = useResponsiveStyles(getImageExperienceStyles, stylingProps);

  const { data } = ads;
  const finalUrls = data && data.FinalUrls;
  const FinalUrlForRecommendation = data && data.FinalUrlForRecommendation;
  const businessName = data && data.BusinessName;
  const mobileFinalUrls = data && data.MobileFinalUrls;
  const headlines = (data && data.Headlines) || [];
  const longHeadlines = (data && data.LongHeadlines) || [];
  const descriptions = (data && data.Descriptions) || [];
  const TrackingUrlTemplate = data && data.TrackingUrlTemplate;
  const FinalUrlSuffix = data && data.FinalUrlSuffix;
  const UrlCustomParameters = data && data.UrlCustomParameters;
  const images = data && data.Images;
  const videos = data && data.videos;
  const impressionTrackingUrls = data && data.ImpressionTrackingUrls;
  const verifiedTrackingSettings = data && data.VerifiedTrackingSettings;
  const hotspots = data && data.hotspots;
  const CallToAction = data && data.CallToAction;
  const CallToActionText = data && data.CallToActionText;
  const CallToActionAriaText = data && data.CallToActionAriaText;
  const BoostAnchors = data && data.BoostAnchors;
  const Campaign = data && data.Campaign;
  const LegacyResponsiveAd = data && data.LegacyResponsiveAd; // true if is RA

  // disable image fitting for boost campaigns
  // disable image fitting for RA
  const isImageFittingEnabled = ImageFittingMVP && !ImageFittingExlcudeMAE && ImageFittingUnifiedImageROI && supportImageFitting && !(IsAmplifyEnabled && DisplayPlusAmplify) && !LegacyResponsiveAd;

  const nativeAdPanels = getNativeAdPanels(serverContext).basicPanels;

  const [
    allAdSettingsSections,
    adSettingsExpandedSections,
    setAdSettingsExpandedSections,
  ] = useCollapseState(
    nativeAdPanels /* all sections */,
    ['images', 'videos', 'logos', 'portrait-images', 'hotspot', 'business-name', 'text', 'call-to-action'] /* initially expanded sections */
  );

  const [
    finalUrlSection,
    finalUrlExpandedSection,
    setFinalUrlExpandedSection,
  ] = useCollapseState(['final-url'], ['final-url']);

  const maxUploadNum = useMemo(() => {
    const imageListLength = images && images.ImageList && isArray(images.ImageList) ? images.ImageList.length : 0;
    return DisplayPlusMaxImageCount - imageListLength;
  }, [images]);

  // copilot states and actions
  const [addNewDescriptionCallback, setAddNewDescriptionCallback] = useState(noop);
  const [addNewShortHeadlinesCallback, setAddNewShortHeadlinesCallback] = useState(noop);
  const [addNewLongHeadlinesCallback, setAddNewLongHeadlinesCallback] = useState(noop);

  const [finalUrlForCopilotRecommendations, setFinalUrlForCopilotRecommendations] = useState(first(finalUrls));

  const [selectedVideoIdx, setSelectedVideoIdx] = useState(null);
  const [selectedValue, setSelectedValue] = useState('imageAds');
  const [imageExperiencePortalContainerState, setImageExperiencePortalContainerState] = useState({});
  const [previewImagesSharedState, setPreviewImagesSharedState] = useState({ previewImageId: null, cropImageObj: null });
  const [imageExperienceAddNewImageCallback, setImageExperienceAddNewImageCallback] = useState(noop);
  const [newCTACallback, setNewCTACallback] = useState(noop);
  const [assetAIEnhancementEnabled, setAssetAIEnhancementEnabled] = useState(false);
  const [assetAIEnhancementSettingsCalled, setAssetAIEnhancementSettingsCalled] = useState(false);
  const [useBrandKit, setUseBrandKit] = useState(true);

  const imagesContainerRef = useRef(null);
  const logosContainerRef = useRef(null);
  const portraitContainerRef = useRef(null);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const legacyI18n = useMemo(() => window.globalLegacyI18n.get('$/i18n!legacy/global'), []);
  const i18nMerged = useMemo(() => mergeI18n(legacyI18n, i18n), [legacyI18n, i18n]);

  const shouldLogosBeEnabled = useMemo(() => _.indexOf(nativeAdPanels, 'logos') !== -1, [nativeAdPanels]);
  const shouldPortraitBeEnabled = useMemo(() => _.indexOf(nativeAdPanels, 'portrait-images') !== -1, [nativeAdPanels]);

  const collectionsSummary = useMemo(() => ([
    { collection: headlines, displayText: i18n.getString(_TL_('short headlines')), displayTextSingular: i18n.getString(_TL_('short headline')) },
    { collection: longHeadlines, displayText: i18n.getString(_TL_('long headlines')), displayTextSingular: i18n.getString(_TL_('long headline')) },
    { collection: descriptions, displayText: i18n.getString(_TL_('descriptions')), displayTextSingular: i18n.getString(_TL_('description')) },
  ]), [headlines, longHeadlines, descriptions, i18n]);

  const textCardSummaryDetails = useMemo(() => getTextCardSummaryDetails(collectionsSummary, i18n), [collectionsSummary, i18n]);

  const hotspotSummaryDetails = useMemo(() => {
    if (!hotspots) {
      return i18n.getString(_TL_('No hotspots added'));
    }
    if (hotspots.length === 0) {
      return i18n.getString(_TL_('No hotspots added'));
    } else if (hotspots.length === 1) {
      return i18n.getString(_TL_('1 hotspot added'));
    }
    return i18n.getString(_TL_('{{count}} hotspots added'), { count: hotspots.length });
  }, [hotspots, i18n]);

  const allAssetsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdSettingsExpandedSections }),
    [setAdSettingsExpandedSections]
  );

  const finalUrlValidationProps = useMemo(() => ({
    data: {
      FinalUrls: finalUrls,
    },
    initialValue: {
      FinalUrls: finalUrls,
    },
    debounce: 400,
  }), [finalUrls]);

  const textAssetsValidationProps = useMemo(() => ({
    data: {
      Headlines: headlines,
      LongHeadlines: longHeadlines,
      Descriptions: descriptions,
    },
  }), [headlines, longHeadlines, descriptions]);

  const ctaValidationProps = useMemo(() => ({
    data: {
      ...CallToAction,
    },
  }), [CallToAction]);

  const onTabSelect = useCallback((event, tab) => {
    setSelectedValue(tab.value);
  }, []);

  const onReset = useCallback(() => {
    const dataPatch = {
      data: {
        data: {
          ...data,
          Images: {
            ...data.Images,
            ImageList: [],
            ImageAssets: null,
          },
          Headlines: {
            Headline0: '',
            Headline1: '',
            Headline2: '',
          },
          LongHeadlines: {
            LongHeadline0: '',
          },
          Descriptions: {
            Description0: '',
            Description1: '',
          },
        },
        isValid: false,
      },
    };

    setAds({
      ...data,
      Images: {
        ...data.Images,
        ImageList: [],
        ImageAssets: null,
      },
      Headlines: {
        Headline0: '',
        Headline1: '',
        Headline2: '',
      },
      LongHeadlines: {
        LongHeadline0: '',
      },
      Descriptions: {
        Description0: '',
        Description1: '',
      },
    });

    onDataChange(dataPatch);
  }, [data, onDataChange]);

  const [shouldShowDisclaimerPanel, setShouldShowDisclaimerPanel] = useState(false);
  const disclaimerRepositoryRef = useRef(new Repository({
    schema: DisclaimerAdExtension,
    skipExtensionCache: true,
  }));

  useEffect(() => {
    if (activity && activity.signal) {
      activity.signal(`[VideoAdsCopilot] - VideoAsAnAsset useBrandKitToggle changed to ${useBrandKit}`);
    }
  }, [useBrandKit, activity]);

  useEffect(() => {
    if (!IsMSANDisclaimerEnabled) {
      setShouldShowDisclaimerPanel(false);
      return null;
    }

    if (IsDomesticSimplifiedChineseEnabled) {
      setShouldShowDisclaimerPanel(false);
      return null;
    }

    if (Campaign && Campaign.Id) {
      return disclaimerRepositoryRef.current.association.findAll({
        where: {
          campaignId: Campaign.Id,
        },
      }).then((disclaimerdata) => {
        if (disclaimerdata && disclaimerdata.length > 0) {
          setShouldShowDisclaimerPanel(true);
        } else {
          setShouldShowDisclaimerPanel(false);
        }
      });
    }
    setShouldShowDisclaimerPanel(false);
    return null;
  }, [Campaign, IsMSANDisclaimerEnabled, IsDomesticSimplifiedChineseEnabled]);

  const {
    copilotBanner,
    suggestionPoller,
  } = useCopilot({
    maxUploadNum,
    addNewShortHeadlinesCallback,
    addNewLongHeadlinesCallback,
    addNewDescriptionCallback,
    imageExperienceAddNewImageCallback,
    newCTACallback,
    currentCTA: CallToAction && CallToAction.cta,
    onReset,
    cid,
    aid,
    serverContext,
    instrumentation,
    data,
    isSaving,
    isCanceling,
    enableCopilotRecommendations: DisplayPlusCopilotRecommendations,
    skipBusinessNameRecommendation: IsAmplifyEnabled && DisplayPlusAmplify,
  });

  const updateFinalUrlForCopilotRecommendations = useCallback(() => {
    // to prevent sending too many requests, only update the copilot recommendations after user has finished editing URL
    if (IsAmplifyEnabled && DisplayPlusAmplify) {
      // for boost campaigns remove the boost prefix from the final url
      const schemeRegex = /^(?!https?:\/\/)[\w-]+:/i;

      // Replace the scheme part with an empty string
      const urlWithoutScheme = FinalUrlForRecommendation.replace(schemeRegex, '');

      setFinalUrlForCopilotRecommendations(urlWithoutScheme);
    } else {
      setFinalUrlForCopilotRecommendations(FinalUrlForRecommendation);
    }
  }, [DisplayPlusAmplify, FinalUrlForRecommendation, IsAmplifyEnabled]);

  const onFinalUrlFocusOut = useCallback(() => {
    updateFinalUrlForCopilotRecommendations();
  }, [updateFinalUrlForCopilotRecommendations]);

  const onFinalUrlKeyUp = useCallback((e) => {
    const { key } = e;
    if (key === 'Enter') {
      updateFinalUrlForCopilotRecommendations();
    }
  }, [updateFinalUrlForCopilotRecommendations]);

  const finalUrlAdditionalProps = useMemo(() => ({
    onBlur: onFinalUrlFocusOut,
    onKeyUp: onFinalUrlKeyUp,
  }), [onFinalUrlKeyUp, onFinalUrlFocusOut]);

  const updateFinalUrlCallback = useCallback((newFinalUrls) => {
    setAds({
      ...data,
      FinalUrls: newFinalUrls,
    });

    onDataChange({
      data: {
        data: {
          ...data,
          FinalUrls: newFinalUrls,
        },
      },
    });
  }, [data, onDataChange]);

  useEffect(() => {
    if (isImageFittingEnabled && !assetAIEnhancementSettingsCalled) {
      setAssetAIEnhancementSettingsCalled(true);
      getAssetEnhancementSettings({ aid, cid, oDataConfig }).then((settings) => {
        if (settings && settings.AssetAIEnhancementOptout) {
          setAssetAIEnhancementEnabled(settings.AssetAIEnhancementOptout === 'false');
        }
      });
    }
  }, [aid, assetAIEnhancementSettingsCalled, cid, isImageFittingEnabled, oDataConfig]);

  // memoized native ads components
  const serverValidatedFinalUrl = useMemo(() => (
    <ServerValidatedFinalUrl
      i18n={i18n}
      validationProps={finalUrlValidationProps}
      permissions={permissions}
      validateOnRender
      isDisplayPlus
      additionalProperties={finalUrlAdditionalProps}
      customUrlProtocol={IsAmplifyEnabled && DisplayPlusAmplify}
    />
  ), [DisplayPlusAmplify, IsAmplifyEnabled, finalUrlAdditionalProps, finalUrlValidationProps, i18n, permissions]);

  const imageListPortalContainer = useMemo(() => (
    <>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={imagesContainerRef}
        containerState={imageExperiencePortalContainerState}
        isLogos={false}
      />
      <PerfMaxImageSuggestionCarousel i18n={i18n} addImageCallback={imageExperienceAddNewImageCallback} permissions={permissions} maxUploadNum={maxUploadNum} isCopilotRecommendations={DisplayPlusCopilotRecommendations} />
    </>
  ), [i18n, imageExperiencePortalContainerState, permissions, imageExperienceAddNewImageCallback, maxUploadNum, DisplayPlusCopilotRecommendations]);

  const storeBackedBusinessName = useMemo(() => (
    <ValidatedStoreBackedBusinessName
      id="input-business-name"
      i18n={i18n}
      validationProps={{
        data: { BusinessName: businessName },
        initialValue: { BusinessName: businessName },
      }}
      formErrorProps={{ filter: nativeAdBusinessNameProp }}
    />
  ), [businessName, i18n]);

  const advancedSettings = useMemo(() => (
    <AdvancedSettings
      mobileFinalUrls={mobileFinalUrls}
      trackingUrlTemplate={TrackingUrlTemplate}
      finalUrlSuffix={FinalUrlSuffix}
      urlCustomParameters={UrlCustomParameters}
      impressionTrackingUrls={impressionTrackingUrls}
      verifiedTrackingSettings={verifiedTrackingSettings}
      biddingScheme={biddingScheme}
      CallToAction={CallToAction}
      CallToActionText={CallToActionText}
      CallToActionAriaText={CallToActionAriaText}
      setNewCTACallback={setNewCTACallback}
      BoostAnchors={BoostAnchors}
      shouldShowDisclaimerPanel={shouldShowDisclaimerPanel}
    />
  ), [mobileFinalUrls, TrackingUrlTemplate, FinalUrlSuffix, UrlCustomParameters, impressionTrackingUrls, verifiedTrackingSettings, biddingScheme, CallToAction, CallToActionText, CallToActionAriaText, BoostAnchors, shouldShowDisclaimerPanel]);

  const placementCustomizationPredicate = useCallback((template) => {
    if ((!isEmpty(template.key)) && (!isEmpty(AdPreviewOverideStylesByKey[template.key]))) {
      return {
        ...template,
        imagePreviewHeight: AdPreviewOverideStylesByKey[template.key].imagePreviewHeight,
      };
    }

    return template;
  }, []);

  let adPreviewConfig = responsiveMsanMMAConfig;
  let placementConfig = msanMMAConfig;
  let adPlacementsGroupType = AdPlacementsGroupType.msanImageAd;

  if (IsAmplifyEnabled && DisplayPlusAmplify) {
    adPreviewConfig = AdPreviewRefresh ? responsiveMsanMMABoostConfigV2 : responsiveMsanMMABoostConfig;
    placementConfig = AdPreviewRefresh ? msanMMABoostConfigV2 : msanMMABoostConfig;
    adPlacementsGroupType = AdPlacementsGroupType.msanBoostImageAd;
  } else if (isMMAV2Enabled) {
    adPreviewConfig = msanMMAV2Config;
    placementConfig = msanMMAV2Config;
    adPlacementsGroupType = AdPlacementsGroupType.msanV2ImageAd;
  }

  const adPreview = useMemo(() => {
    const PerfMaxAdPreviewComponent = UAPLNativeAdIntegration ? PerfMaxAdPreviewUAPL : PerfMaxAdPreview;

    const PerfMaxAdPreviewElement = (
      <PerfMaxAdPreviewComponent
        i18n={i18n}
        imageList={images && images.ImageList}
        hotspots={hotspots}
        updateImageList={noop}
        shortHeadline={headlines && headlines.Headline0}
        longHeadline={longHeadlines && longHeadlines.LongHeadline0}
        description={descriptions && descriptions.Description0}
        businessName={businessName}
        displayUrl={finalUrls}
        url={finalUrls}
        previewImagesSharedState={previewImagesSharedState}
        setPreviewImagesSharedState={setPreviewImagesSharedState}
        isMSANAd
        enableAutoPlay={false}
        callToAction={CallToAction && CallToAction.cta}
        placementCustomizationPredicate={placementCustomizationPredicate}
        adPlacementsGroupType={adPlacementsGroupType}
        adPreviewConfig={adPreviewConfig}
        extraPlacementInfo={extraPlacementInfo}
        customPlacementHeaderClassName={getClasses('adPreviewPlacementHeader')}
        customPlacementContainerClassName={getClasses('adPreviewPlacementContainer')}
        isMMAV2Enabled={isMMAV2Enabled}
        activity={activity}
        isImageFittingEnabled={isImageFittingEnabled && assetAIEnhancementEnabled}
      />
    );

    if (!permissions.dynamic.VideoAsAnAsset || IsAmplifyEnabled) {
      return PerfMaxAdPreviewElement;
    }

    return (
      <div>
        <TabList selectedValue={selectedValue} onTabSelect={onTabSelect}>
          <Tab id={TAB_IMAGE_ADS} value={TAB_IMAGE_ADS}>{i18n.getString(_TL_('Image ads'))}</Tab>
          <Tab id={TAB_VIDEO_ADS} value={TAB_VIDEO_ADS}><b>{i18n.getString(_TL_('Video ads'))}</b></Tab>
        </TabList>
        {selectedValue === TAB_IMAGE_ADS && PerfMaxAdPreviewElement}
        {selectedValue === TAB_VIDEO_ADS && (
          <MultiAdVideoPreview
            i18n={i18n}
            selectedVideoIdx={selectedVideoIdx}
            data={data}
            loadClipchampPlayer={loadClipchampPlayer}
            permissions={permissions}
          />
        )}
      </div>
    );
  }, [UAPLNativeAdIntegration, i18n, images, hotspots, headlines, longHeadlines, descriptions, businessName, finalUrls, previewImagesSharedState, CallToAction, placementCustomizationPredicate, adPlacementsGroupType, adPreviewConfig, getClasses, isMMAV2Enabled, activity, isImageFittingEnabled, assetAIEnhancementEnabled, permissions.dynamic.VideoAsAnAsset, IsAmplifyEnabled, selectedValue, onTabSelect, selectedVideoIdx, data]);

  const validatedImageExperienceWrapper = useMemo(() => {
    let mappedPreviewImageSharedState = previewImagesSharedState;
    const { editingPlacementType } = previewImagesSharedState || {};
    if (editingPlacementType && extraPlacementInfo[editingPlacementType]) {
      // in editing panel, show normal placement components
      mappedPreviewImageSharedState = {
        ...previewImagesSharedState,
        editingPlacementType: extraPlacementInfo[editingPlacementType].editingPlacementType,
      };
    }

    return (
      <ValidatedImageExperienceWrapper
        i18n={i18n}
        permissions={permissions}
        imagesContaineRef={imagesContainerRef}
        logosContaineRef={logosContainerRef}
        portraitContainerRef={portraitContainerRef}
        isLogoEnabled={shouldLogosBeEnabled}
        isBoostPortraitEnabled={shouldPortraitBeEnabled}
        updateFinalUrlCallback={updateFinalUrlCallback}
        previewImagesSharedState={mappedPreviewImageSharedState}
        setPreviewImagesSharedState={setPreviewImagesSharedState}
        updatePortalContainerState={setImageExperiencePortalContainerState}
        setImageExperienceAddNewImageCallback={setImageExperienceAddNewImageCallback}
        validationProps={{ data, initialValue: data }}
        suggestionPoller={suggestionPoller}
        isCopilotRecommendations={DisplayPlusCopilotRecommendations}
        wizardPerfMarker={perfMarker}
        isCombinedMsanAndMmaAspectRatios={false}
        customMaxImageCount={DisplayPlusMaxImageCount}
        isMSANBoostEnabled={IsAmplifyEnabled && DisplayPlusAmplify}
        UAPLPMaxIntegration={UAPLNativeAdIntegration}
        isAudienceAd
        adPreviewConfig={{
          adType: IsAmplifyEnabled && DisplayPlusAmplify ? 'Boost' : 'AudienceAd',
          placementConfig,
        }}
        isMMAV2Enabled={isMMAV2Enabled}
        showCopilotOptionForImage={permissions.IsBackgroundSceneEnabled}
        customAssetLibraryEventHandler={{
          onUploadImage: (anActivity, imgId) => {
            anActivity.signal(`display plus native ad editor: uploaded image, isEditing:${!!editingAdId}, image id:${imgId}`);
          },
          onImageListUpdate: (anActivity, newImageList, curImageList) => {
            // log when new images added in audience ads editor
            // eslint-disable-next-line newline-per-chained-call
            const newImageIdList = _.chain(newImageList).flatten().map(img => `${img.id}`).uniq().sortBy().value();
            // eslint-disable-next-line newline-per-chained-call
            const curImageIdList = _.chain(curImageList).flatten().map(img => `${img.id}`).uniq().sortBy().value();
            const newImages = _.difference(newImageIdList, curImageIdList);
            if (newImages.length > 0) {
              anActivity.signal(`display plus native ad editor: added images, isEditing:${!!editingAdId}, image ids:${newImages.join(',')}, image count:${newImages.length}`);
            }
          },
        }}
        supportImageUpscale={permissions.dynamic.AudienceImageUpscale}
        isImageFittingEnabled={isImageFittingEnabled && assetAIEnhancementEnabled}
      />
    );
  }, [previewImagesSharedState, i18n, permissions, shouldLogosBeEnabled, shouldPortraitBeEnabled, updateFinalUrlCallback, data, suggestionPoller, DisplayPlusCopilotRecommendations, perfMarker, IsAmplifyEnabled, DisplayPlusAmplify, UAPLNativeAdIntegration, placementConfig, isMMAV2Enabled, isImageFittingEnabled, assetAIEnhancementEnabled, editingAdId]);

  const textAssetCollectionWithCopilot = useMemo(() => (
    <TextAssetCollectionWithCoPilot
      validationProps={textAssetsValidationProps}
      setAddNewDescriptionCallback={setAddNewDescriptionCallback}
      setAddNewShortHeadlinesCallback={setAddNewShortHeadlinesCallback}
      setAddNewLongHeadlinesCallback={setAddNewLongHeadlinesCallback}
      isAudienceAd
    />
  ), [textAssetsValidationProps]);

  const logosContainer = useMemo(() => (
    <>
      <span>{i18n.getString(_TL_('You can add up to 5 different logos. Each logo can have one or both 4:1 aspect ratio and 1:1 aspect ratio.'))}</span>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={logosContainerRef}
        containerState={imageExperiencePortalContainerState}
        isLogos
      />
    </>
  ), [i18n, imageExperiencePortalContainerState]);

  const portraitImageContainer = useMemo(() => (
    <>
      <span> {i18n.getString(_TL_('You can add one portrait image'))} </span>
      <ImageListPortalContainerWithControlErrorHandling
        i18n={i18n}
        containerRef={portraitContainerRef}
        containerState={imageExperiencePortalContainerState}
        isLogos={false}
      />
    </>
  ), [i18n, imageExperiencePortalContainerState]);

  const hotspot = useMemo(() => (
    <>
      <ValidatedHotspot
        validationProps={{
          data: {
            hotspots,
          },
        }}
      />
    </>
  ), [hotspots]);

  const videosContainer = useMemo(() => {
    const videoAd = { videos: videos || [] };

    return (
      <CampaignFetchProvider>
        <ValidatedVideoExperienceWithRecommendationFetch
          i18n={i18nMerged}
          perfMarker={perfMarker}
          permissions={permissions}
          oDataConfig={oDataConfig}
          cid={cid}
          aid={aid}
          multiVideoMode
          allowedVideoSizes={videoTypesWithOrder}
          disableRemoveAll
          hideRatioLabel
          videoAd={videoAd}
          videoExperienceCircleUploadType
          disableUploadTextField
          videoPickerContainerHeight="auto"
          copilotEnabled
          setSelectedVideoIdx={setSelectedVideoIdx}
          loadClipchampPlayer={loadClipchampPlayer}
          useBrandKit={useBrandKit}
          setUseBrandKit={setUseBrandKit}
          serverContext={serverContext}
        />
      </CampaignFetchProvider>
    );
  }, [videos, i18nMerged, perfMarker, permissions, oDataConfig, cid, aid, useBrandKit, setUseBrandKit, loadClipchampPlayer, serverContext]);

  return (
    <div className="display-plus-native-ads">
      {data && (
        <CopilotRepositoryProvider
          campaignId="-1"
          finalUrl={finalUrlForCopilotRecommendations}
          locale={i18n.getLocale()}
          recommendationType="Native"
          serverContext={serverContext}
          enableLogoAndCTA={CopilotCTALogoGeneration && IsMSANLogoAndCTAForMMAEnabled}
          useClipchamp={useClipchamp}
        >
          <div className={classNames(getClasses('container'))}>
            <div className={classNames(getClasses('leftStyles'))}>
              <Collapse allPanelItems={finalUrlSection} checkItems skipItemsTypeCheck activeKeys={finalUrlExpandedSection} setActiveKeys={setFinalUrlExpandedSection}>
                <ValidatedPanel
                  key="final-url"
                  title={<TextTitle titleKey={_TL_('Final URL')} topicKey="pop_BA_NewAdCreate_FinalURL" details={getFinalUrlCardSummaryDetails(i18n, finalUrls)} icon={FinalUrlImg} />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'final-url',
                  }}
                >
                  <div className="final-url-container">
                    {serverValidatedFinalUrl}
                  </div>
                </ValidatedPanel>
              </Collapse>

              {
                DisplayPlusCopilotRecommendations && (copilotBanner)
              }
              <Collapse allPanelItems={allAdSettingsSections} checkItems skipItemsTypeCheck activeKeys={adSettingsExpandedSections} setActiveKeys={setAdSettingsExpandedSections}>
                <ValidatedPanel
                  key="images"
                  title={<TextTitle
                    titleKey={_TL_('Images')}
                    topicKey="pop_BA_ImageAssets_Audience"
                    icon={ImageAssetsImg}
                    iconAltText={i18n.getString(_TL_('"Images" panel\'s image'))}
                    details={<ImageListSummaryCardDetails i18n={i18n} images={images} isLogos={false} />}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'images',
                  }}
                >
                  <span>{i18n.getString(_TL_('You can add up to {{limit}} images to your ad.'), { limit: DisplayPlusMaxImageCount })}</span>
                  <div className={classNames(getClasses('imageExperienceStyle'))}>
                    {imageListPortalContainer}
                  </div>
                </ValidatedPanel>
                <ValidatedPanel
                  key="videos"
                  title={<TextTitle
                    titleKey={_TL_('Videos')}
                    topicKey="pop_BA_VideoAds_VideoSource"
                    icon={VideoAssetsImg}
                    iconAltText={i18n.getString(_TL_('"Videos" panel\'s image'))}
                    details={<ImageListSummaryCardDetails i18n={i18n} videos={videos} isLogos={false} isVideos />}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'videos',
                  }}
                >
                  <span>{i18n.getString(_TL_('You can add up to {{limit}} videos to your ad.'), { limit: DisplayPlusMaxVideoCount })}</span>
                  <div className={classNames(getClasses('imageExperienceStyle'))}>
                    {videosContainer}
                  </div>
                </ValidatedPanel>
                <ValidatedPanel
                  key="logos"
                  title={<TextTitle
                    titleKey={_TL_('Logos')}
                    icon={LogoAssetsImg}
                    iconAltText={i18n.getString(_TL_('"Logos" panel\'s image'))}
                    topicKey="pop_BA_AudAds_Logos"
                    details={<ImageListSummaryCardDetails i18n={i18n} images={images} isLogos />}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'logos',
                  }}
                >
                  {logosContainer}
                </ValidatedPanel>
                <ValidatedPanel
                  key="portrait-images"
                  title={<TextTitle
                    titleKey={_TL_('Portrait image')}
                    icon={LogoAssetsImg}
                    iconAltText={i18n.getString(_TL_('"Portrait" panel\'s image'))}
                    details={<ImageListSummaryCardDetails i18n={i18n} images={images} isPortrait />}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'portrait-images',
                  }}
                >
                  {portraitImageContainer}
                </ValidatedPanel>
                <ValidatedPanel
                  key="text"
                  title={<TextTitle
                    titleKey={_TL_('Text')}
                    topicKey="pop_BA_AudAds_Text"
                    icon={TextAssetsImg}
                    iconAltText={i18n.getString(_TL_('"Text" panel\'s image'))}
                    details={textCardSummaryDetails}
                  />}
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'text',
                  }}
                >
                  {
                    DisplayPlusCopilotRecommendations ? (
                      textAssetCollectionWithCopilot
                    ) : (
                      <div className={classNames(getClasses('textAssetStyle'))}>
                        <TextAssetCollectionWithRecommendationPrefill
                          validationProps={textAssetsValidationProps}
                          isOptional={false}
                          showDeleteButton
                          withInlineRecommendations
                          isAudienceAd
                        />
                      </div>
                    )
                  }
                </ValidatedPanel>
                <ValidatedPanel
                  key="hotspot-panel"
                  title={
                    <TextTitle
                      titleKey={_TL_('Hotspot')}
                      icon={hotspotImg}
                      details={hotspotSummaryDetails}
                    />
                  }
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                  actuationProps={{
                    expand: allAssetsExpand,
                    expandKey: 'hotspot',
                  }}
                >
                  {hotspot}
                </ValidatedPanel>
                <ValidatedPanel
                  key="business-name"
                  title={
                    <TextTitle
                      titleKey={_TL_('Business name')}
                      topicKey="pop_BA_CampaignCreate_AudAds_BusinessName"
                      icon={BusinessNameImg}
                      iconAltText={i18n.getString(_TL_('"Business name" panel\'s image'))}
                    />
                  }
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                >
                  <div className={classNames(getClasses('businessNameStyle'))}>
                    {storeBackedBusinessName}
                  </div>
                </ValidatedPanel>
                <ValidatedFixedPanel
                  key="call-to-action"
                  title={
                    <TextTitle
                      titleKey={_TL_('Call to action')}
                      topicKey="pop_BA_AudAds_CTA"
                      icon={CallToActionImg}
                      iconAltText={i18n.getString(_TL_('"Call to action" panel\'s image'))}
                    />
                  }
                  noOverflowHidden
                  relativePosition
                  attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                >
                  <ValidatedLegacyCallToActionWithLanguage
                    permissions={permissions}
                    validationProps={ctaValidationProps}
                    isDisplayPlusMMA
                    i18n={i18n}
                    setNewCTACallback={setNewCTACallback}
                  />
                </ValidatedFixedPanel>
              </Collapse>
              <div className={advancedSettingsStyles}>
                {advancedSettings}
              </div>
            </div>
            <div className={classNames(getClasses('rightStyles'))}>
              <div className={classNames(getClasses('nativeAdImagePreviewCarousel'))}>
                {adPreview}
              </div>
              <div>
                {validatedImageExperienceWrapper}
              </div>
            </div>
          </div>
        </CopilotRepositoryProvider>
      )}
    </div>
  );
};

DisplayPlusNativeAdsInternal.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({
      FinalUrls: PropTypes.string,
      MobileFinalUrls: PropTypes.string,
      BusinessName: PropTypes.string,
      Headlines: PropTypes.arrayOf(PropTypes.string),
      LongHeadlines: PropTypes.arrayOf(PropTypes.string),
      Descriptions: PropTypes.arrayOf(PropTypes.string),
      Images: PropTypes.shape({}),
      videos: PropTypes.shape({}),
      TrackingUrlTemplate: PropTypes.string,
      FinalUrlSuffix: PropTypes.string,
      UrlCustomParameters: PropTypes.string,
      ImpressionTrackingUrls: PropTypes.string,
      VerifiedTrackingSettings: PropTypes.shape({}),
      FinalUrlForRecommendation: PropTypes.string,
      CallToAction: PropTypes.shape({
        cta: PropTypes.string,
        language: PropTypes.string,
      }),
      CallToActionText: PropTypes.string,
      CallToActionAriaText: PropTypes.string,
      hotspots: PropTypes.shape({}),
      BoostAnchors: PropTypes.shape({}),
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.string,
      aid: PropTypes.string,
    }),
  }).isRequired,
  isSaving: PropTypes.bool.isRequired,
  isCanceling: PropTypes.bool.isRequired,
  biddingScheme: PropTypes.string,
  editingAdId: PropTypes.string,
  activity: PropTypes.shape({}),
  supportImageFitting: PropTypes.bool,
  setAds: PropTypes.func,
};

DisplayPlusNativeAdsInternal.defaultProps = {
  biddingScheme: null,
  editingAdId: null,
  activity: undefined,
  supportImageFitting: false,
  setAds: () => { },
};

export const DisplayPlusNativeAds = withActivityContext(withBannerService(withScope(DisplayPlusNativeAdsInternal)));
